import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { IconArrowLeft, IconPencil } from "@tabler/icons-react"
import { Link } from "@tanstack/react-router"
import { formatDateTime } from "../../lib/format-date"
import { ConnectionPill, type TConnectionHealth } from "./ConnectionPill"
import { StatusPill, type TRecordingPhase } from "./StatusPill"
import classes from "./Topbar.module.css"

interface IProps {
  phase: TRecordingPhase
  subject: string
  startTime: string | null
  /** Optional rename handler — when provided, a pencil button is shown next to the name. */
  onRename?: () => void
  /** Override the back link with a callback (e.g. to show an exit-confirm). When omitted the Link
   * navigates to "/" directly. */
  onExit?: () => void
  /** Override the default "All meetings" / "Exit focus" back-link copy. */
  exitLabel?: React.ReactNode
  /** Dimmed hint after the meeting name (e.g. "names itself when it ends"). */
  hint?: React.ReactNode
  /** Aggregate recording-health status — drives the connection chip during live phases. */
  health?: TConnectionHealth
  /** True while the live-transcription WebSocket is in its reconnect loop. */
  wsReconnecting?: boolean
  /** Outstanding health issues — surfaced in the connection chip's popover. */
  healthIssues?: string[]
}

export function Topbar({
  phase,
  subject,
  startTime,
  onRename,
  onExit,
  exitLabel: exitLabelProp,
  hint,
  health,
  wsReconnecting = false,
  healthIssues = [],
}: IProps): React.JSX.Element {
  const { i18n } = useLingui()
  // Compact, deterministic, locale-aware start label (no year) — see lib/format-date.
  const dateLabel = formatDateTime(i18n, startTime, { year: false })

  const exitLabel = exitLabelProp ?? (phase === "prerecord" ? <Trans>All meetings</Trans> : <Trans>Exit focus</Trans>)

  return (
    <div className={classes.topbar}>
      {onExit ? (
        <button type="button" className={classes.exit} onClick={onExit}>
          <IconArrowLeft size={14} />
          {exitLabel}
        </button>
      ) : (
        <Link to="/" className={classes.exit}>
          <IconArrowLeft size={14} />
          {exitLabel}
        </Link>
      )}

      <div className={classes.identifier}>
        <span className={classes.name}>{subject}</span>
        {dateLabel && (
          <>
            <span className={classes.divider} aria-hidden="true">
              ·
            </span>
            <span className={classes.date}>{dateLabel}</span>
          </>
        )}
        {hint ? <span className={classes.hint}>{hint}</span> : null}
        {onRename && (
          <button type="button" className={classes.editBtn} onClick={onRename} aria-label={i18n._(msg`Rename meeting`)}>
            <IconPencil size={13} />
          </button>
        )}
      </div>

      <div className={classes.spacer} />
      {phase !== "prerecord" && health && (
        <ConnectionPill status={health} wsReconnecting={wsReconnecting} issues={healthIssues} />
      )}
      {/* "Recording" already sits next to the timer. The pill duplicated it.
          Paused and finishing still use the pill; Connected stays on the connection chip. */}
      {phase === "recording" ? null : <StatusPill phase={phase} />}
    </div>
  )
}
